import React from "react";
import { createRoot } from "react-dom/client";
import "./styles.css";
import data from "./data/data.json";
import ErrorBoundary from "./error/ErrorBoundary";
import PropsInspector from "./comp/PropsInspector";
import widgetComponents from "./data/index"
import FrenchPrefixHandler from "./hooks/FrenchPrefixHandler";

// Widget registry with metadata
const widgetsRegistry = data;

// Performance configuration
const CONFIG = {
  intersectionMargin: "200px",
  debounceTime: 100,
  idleTimeout: 1000,
  loadingDelay: 200,
  maxRetries: 3,
  retryDelay: 1000,
  devMode: window.location.hostname === 'localhost',
};

// Enhanced state management
const widgetState = {
  initialized: new Set(),
  inProgress: new Set(),
  failed: new Set(),
  retryCount: new Map(),
  preserveWidgets: false,
  isNavigating: false,
  navigationTimeout: null,
  initializationQueue: new Set(),
  isReady: false
};

// ----------------
// Props Inspector Manager (Simplified)
// ----------------
class PropsInspectorManager {
  constructor() {
    this.inspectedWidgets = new Map();
    this.renderCount = new Map();
    this.propsHistory = new Map();
    this.performanceData = new Map();
    this.inspectorRoot = null;
    
    if (CONFIG.devMode) {
      this.initializeInspector();
    }
  }

  initializeInspector() {
    // Create container for Props Inspector
    const inspectorContainer = document.createElement('div');
    inspectorContainer.id = 'props-inspector-root';
    document.body.appendChild(inspectorContainer);
    
    // Create React root for Props Inspector
    this.inspectorRoot = createRoot(inspectorContainer);
    this.renderInspector();
  }

  renderInspector() {
    if (!this.inspectorRoot) return;
    
    this.inspectorRoot.render(
      <></>
    );
  }

  trackWidget(containerId, widgetType, props, container) {
    if (!CONFIG.devMode) return;
    
    const currentCount = this.renderCount.get(containerId) || 0;
    this.renderCount.set(containerId, currentCount + 1);
    
    // Store props history
    const history = this.propsHistory.get(containerId) || [];
    history.push({
      timestamp: Date.now(),
      props: JSON.parse(JSON.stringify(props)),
      renderCount: currentCount + 1
    });
    
    // Keep only last 10 renders
    if (history.length > 10) history.shift();
    this.propsHistory.set(containerId, history);
    
    // Calculate performance metrics
    this.calculatePerformance(containerId, props);
    
    // Store widget info
    this.inspectedWidgets.set(containerId, {
      widgetType,
      props,
      container,
      lastUpdate: Date.now(),
      element: container
    });
    
    // Re-render inspector
    this.renderInspector();
  }

  calculatePerformance(containerId, props) {
    const renderCount = this.renderCount.get(containerId) || 0;
    const propsSize = JSON.stringify(props).length;
    
    // Enhanced performance scoring
    let score = 100;
    
    // Render frequency (more sensitive)
    if (renderCount > 3) score -= 15;
    if (renderCount > 5) score -= 25;
    if (renderCount > 10) score -= 40;
    
    // Props complexity
    if (propsSize > 5000) score -= 10;   // 5KB threshold
    if (propsSize > 10000) score -= 20;  // 10KB threshold
    if (propsSize > 20000) score -= 30;  // 20KB threshold
    
    // Component load time (if available)
    const loadTime = performance.now() - (this.loadStartTime || 0);
    if (loadTime > 100) score -= 10;
    if (loadTime > 500) score -= 25;
    
    // Memory considerations
    const hasComplexObjects = this.hasComplexObjects(props);
    if (hasComplexObjects) score -= 5;
    
    // Bundle size estimate (rough calculation)
    const estimatedBundleImpact = this.estimateBundleSize(props);
    if (estimatedBundleImpact > 50000) score -= 15; // 50KB+
    
    score = Math.max(0, score);
    
    const warnings = [
      ...(renderCount > 3 ? ['Frequent re-renders'] : []),
      ...(renderCount > 10 ? ['Excessive re-renders'] : []),
      ...(propsSize > 10000 ? ['Large props detected'] : []),
      ...(loadTime > 500 ? ['Slow load time'] : []),
      ...(hasComplexObjects ? ['Complex object props'] : []),
      ...(estimatedBundleImpact > 50000 ? ['Large bundle impact'] : [])
    ];

    this.performanceData.set(containerId, {
      score,
      renderCount,
      propsSize,
      loadTime: loadTime > 0 ? `${Math.round(loadTime)}ms` : 'N/A',
      warnings,
      bundleImpact: estimatedBundleImpact
    });
  }

  hasComplexObjects(props) {
    return Object.values(props).some(value => {
      if (Array.isArray(value)) return value.length > 10;
      if (typeof value === 'object' && value !== null) {
        return Object.keys(value).length > 5 || JSON.stringify(value).length > 1000;
      }
      return false;
    });
  }

  estimateBundleSize(props) {
    // Rough estimation based on prop complexity
    let size = JSON.stringify(props).length;
    
    // Add estimated component overhead
    size += 5000; // Base component size
    
    // Add complexity multiplier
    const complexity = Object.keys(props).length;
    size += complexity * 100;
    
    return size;
  }

  highlightWidget(containerId) {
    const container = document.getElementById(containerId);
    if (!container) return;
    
    // Remove existing highlights
    document.querySelectorAll('.cw-widget-highlight').forEach(el => el.remove());
    
    // Create highlight overlay
    const highlight = document.createElement('div');
    highlight.className = 'cw-widget-highlight';
    highlight.style.cssText = `
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(33, 150, 243, 0.3);
      border: 2px solid #2196F3;
      pointer-events: none;
      z-index: 9998;
      animation: cwHighlight 2s ease-in-out;
    `;
    
    // Add keyframes if not exists
    if (!document.getElementById('cw-highlight-keyframes')) {
      const style = document.createElement('style');
      style.id = 'cw-highlight-keyframes';
      style.textContent = `
        @keyframes cwHighlight {
          0%, 100% { opacity: 0; transform: scale(1); }
          50% { opacity: 1; transform: scale(1.02); }
        }
      `;
      document.head.appendChild(style);
    }
    
    container.style.position = 'relative';
    container.appendChild(highlight);
    
    // Scroll to widget
    container.scrollIntoView({ behavior: 'smooth', block: 'center' });
    
    // Remove highlight after animation
    setTimeout(() => {
      if (highlight.parentNode) {
        highlight.parentNode.removeChild(highlight);
      }
    }, 2000);
  }

  copyProps(containerId) {
    const widget = this.inspectedWidgets.get(containerId);
    if (!widget) return;
    
    const propsJson = JSON.stringify(widget.props, null, 2);
    navigator.clipboard.writeText(propsJson).then(() => {
      // Show toast notification
      this.showToast('✅ Props copied to clipboard!', 'success');
    }).catch(() => {
      this.showToast('❌ Failed to copy props', 'error');
    });
  }

  showToast(message, type = 'info') {
    const toast = document.createElement('div');
    toast.innerHTML = message;
    toast.className = `cw-fixed cw-top-1/2 cw-left-1/2 cw-transform cw--translate-x-1/2 cw--translate-y-1/2 cw-px-6 cw-py-3 cw-rounded-lg cw-z-[10001] cw-font-sans cw-text-white cw-shadow-lg`;
    
    const bgColor = type === 'success' ? 'cw-bg-green-500' : 
                   type === 'error' ? 'cw-bg-red-500' : 'cw-bg-blue-500';
    toast.classList.add(bgColor);
    
    document.body.appendChild(toast);
    setTimeout(() => document.body.removeChild(toast), 2000);
  }
}

// Initialize Props Inspector
const propsInspector = new PropsInspectorManager();
window.propsInspector = propsInspector;

// ----------------
// Core utilities
// ----------------
const componentCache = new Map();

const debounce = (fn, delay) => {
  let timer = null;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), delay);
  };
};

const isInViewport = (el, offset = 300) => {
  if (!el) return false;
  const rect = el.getBoundingClientRect();
  return rect.top - offset < window.innerHeight && rect.bottom + offset > 0;
};

const supports = {
  observer: "IntersectionObserver" in window,
  idle: "requestIdleCallback" in window,
};

// ----------------
// Enhanced State Management
// ----------------
const getContainerState = (container) => {
  if (!container || !container.id) return null;
  
  return {
    id: container.id,
    initialized: widgetState.initialized.has(container.id),
    inProgress: widgetState.inProgress.has(container.id),
    failed: widgetState.failed.has(container.id),
    hasRoot: !!container.__reactRoot,
    retryCount: widgetState.retryCount.get(container.id) || 0
  };
};

const setContainerState = (container, state) => {
  if (!container || !container.id) return;
  
  const id = container.id;
  
  if (state.initialized !== undefined) {
    if (state.initialized) {
      widgetState.initialized.add(id);
    } else {
      widgetState.initialized.delete(id);
    }
  }
  
  if (state.inProgress !== undefined) {
    if (state.inProgress) {
      widgetState.inProgress.add(id);
    } else {
      widgetState.inProgress.delete(id);
    }
  }
  
  if (state.failed !== undefined) {
    if (state.failed) {
      widgetState.failed.add(id);
    } else {
      widgetState.failed.delete(id);
    }
  }
  
  if (state.retryCount !== undefined) {
    widgetState.retryCount.set(id, state.retryCount);
  }
};

const resetContainerState = (container) => {
  if (!container || !container.id) return;
  
  const id = container.id;
  widgetState.initialized.delete(id);
  widgetState.inProgress.delete(id);
  widgetState.failed.delete(id);
  widgetState.retryCount.delete(id);
  widgetState.initializationQueue.delete(id);
  
  delete container.__reactRoot;
  delete container.__cleanup;
};

// ----------------
// Navigation Handling - Enhanced
// ----------------
const handleElementorNavigation = () => {
  if (!window.elementorFrontend) return;
  
  // Enhanced Elementor navigation detection
  const elementorHooks = window.elementorFrontend.hooks;
  
  // Before any Elementor operation
  elementorHooks.addAction('frontend/element_ready/global', () => {
    if (widgetState.navigationTimeout) {
      clearTimeout(widgetState.navigationTimeout);
      widgetState.navigationTimeout = null;
    }
    widgetState.preserveWidgets = false;
  });

  // Detect page transitions
  if (window.elementorFrontend.modules?.pageTransition) {
    const pageTransition = window.elementorFrontend.modules.pageTransition;
    
    // Hook into page transition events
    elementorHooks.addAction('pageTransition/beforeTransition', () => {
      // console.log('🔄 Elementor page transition starting...');
      widgetState.preserveWidgets = true;
      widgetState.isNavigating = true;
    });
    
    elementorHooks.addAction('pageTransition/afterTransition', () => {
      // console.log('✅ Elementor page transition complete, reinitializing...');
      setTimeout(() => {
        widgetState.preserveWidgets = false;
        widgetState.isNavigating = false;
        performFullReinitialization();
      }, 100);
    });
    
    // Override replaceContent method for better control
    const originalReplaceContent = pageTransition.replaceContent;
    if (originalReplaceContent) {
      pageTransition.replaceContent = function(...args) {
        // console.log('🔄 Elementor replaceContent called, preserving widgets...');
        widgetState.preserveWidgets = true;
        widgetState.isNavigating = true;
        
        const result = originalReplaceContent.apply(this, args);
        
        // Ensure reinitialization happens
        setTimeout(() => {
          widgetState.preserveWidgets = false;
          widgetState.isNavigating = false;
          performFullReinitialization();
        }, 150);
        
        return result;
      };
    }
  }
  
  // Handle Elementor Pro page transitions
  if (window.elementorProFrontend?.hooks) {
    window.elementorProFrontend.hooks.addAction('pageTransition/beforeTransition', () => {
      widgetState.preserveWidgets = true;
      widgetState.isNavigating = true;
    });

    window.elementorProFrontend.hooks.addAction('pageTransition/afterTransition', () => {
      setTimeout(() => {
        widgetState.preserveWidgets = false;
        widgetState.isNavigating = false;
        performFullReinitialization();
      }, 100);
    });
  }
};

const handleLinkClicks = () => {
  document.addEventListener('click', (event) => {
    const link = event.target.closest('a');
    if (link && link.href && !link.href.startsWith('#') && !link.hasAttribute('data-no-cleanup')) {
      const isExternal = link.hostname !== window.location.hostname;
      const isDownload = link.hasAttribute('download');
      const isMailto = link.href.startsWith('mailto:');
      const isTel = link.href.startsWith('tel:');
      
      if (!isExternal && !isDownload && !isMailto && !isTel) {
        // console.log('🔗 Internal navigation detected...');
        widgetState.preserveWidgets = true;
        widgetState.isNavigating = true;
        
        if (widgetState.navigationTimeout) {
          clearTimeout(widgetState.navigationTimeout);
        }
        
        widgetState.navigationTimeout = setTimeout(() => {
          widgetState.preserveWidgets = false;
          widgetState.isNavigating = false;
          widgetState.navigationTimeout = null;
        }, 5000);
      }
    }
  }, true);
};

// ----------------
// Enhanced Mutation Observer
// ----------------
const observeContainerChanges = () => {
  if (!window.MutationObserver) return;
  
  const observer = new MutationObserver((mutations) => {
    let shouldReinitialize = false;
    const addedContainers = new Set();
    
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType === Node.ELEMENT_NODE) {
          // Check if the node itself is a widget container
          if (node.classList && Array.from(node.classList).some(cls => 
            cls.includes('custom-') && cls.includes('-widget-container'))) {
            addedContainers.add(node);
            shouldReinitialize = true;
          }
          
          // Check for widget containers within the node
          if (node.querySelectorAll) {
            const containers = node.querySelectorAll('[class*="custom-"][class*="-widget-container"]');
            containers.forEach(container => {
              addedContainers.add(container);
              shouldReinitialize = true;
            });
          }
        }
      });
      
      mutation.removedNodes.forEach((node) => {
        if (node.nodeType === Node.ELEMENT_NODE && node.id) {
          // Clean up state for removed nodes
          resetContainerState(node);
        }
      });
    });
    
    if (shouldReinitialize && !widgetState.preserveWidgets && !widgetState.isNavigating) {
      // console.log(`🔄 DOM mutations detected, processing ${addedContainers.size} containers...`);
      
      // Process new containers immediately
      addedContainers.forEach(container => {
        if (container.id && !getContainerState(container).initialized) {
          widgetState.initializationQueue.add(container.id);
        }
      });
      
      // Batch process after a short delay
      setTimeout(() => {
        processInitializationQueue();
      }, 50);
    }
  });
  
  observer.observe(document.body, {
    childList: true,
    subtree: true
  });
  
  return observer;
};

// ----------------
// Data handling - Enhanced
// ----------------
const parseJson = (str, fallback = null) => {
  if (!str || typeof str !== 'string') return fallback;
  
  try {
    return JSON.parse(str);
  } catch (e) {
    console.warn('Failed to parse JSON:', str.substring(0, 100) + '...', e);
    return fallback;
  }
};

const convertValue = (val) => {
  if (val === "true") return true;
  if (val === "false") return false;
  if (val === "null") return null;
  if (val === "undefined") return undefined;
  if (!isNaN(val) && val !== "" && !isNaN(parseFloat(val))) {
    return parseFloat(val);
  }
  return val;
};

const convertDataAttributeToKey = (dataKey) => {
  return dataKey
    .replace(/^data-/, '')
    .replace(/-([a-z])/g, (g) => g[1].toUpperCase());
};

const extractProps = (container, widget) => {
  const props = { ...widget.defaultProps };
  const { dataset } = container;
  
  const isDebugMode = CONFIG.devMode && container.dataset.widgetType === 'faqs_widget';
  
  if (isDebugMode) {
    // console.log('🔍 Extracting props for:', container.id);
    // console.log('Container dataset:', dataset);
    // console.log('Widget config:', { propKeys: widget.propKeys, jsonProps: widget.jsonProps });
  }

  widget.propKeys?.forEach((key) => {
    const possibleKeys = [
      key,
      key.replace(/([A-Z])/g, '-$1').toLowerCase(),
      key.toLowerCase()
    ];
    
    let foundValue = null;
    let foundKey = null;
    
    for (const testKey of possibleKeys) {
      if (testKey in dataset) {
        foundValue = dataset[testKey];
        foundKey = testKey;
        break;
      }
    }
    
    if (foundValue !== null) {
      if (widget.jsonProps?.includes(key)) {
        const parsedValue = parseJson(foundValue, widget.defaultProps[key]);
        props[key] = parsedValue;
        
        if (isDebugMode) {
          // console.log(`✅ JSON prop ${key}:`, parsedValue);
        }
      } else {
        props[key] = convertValue(foundValue);
        
        if (isDebugMode) {
          // console.log(`✅ Regular prop ${key}:`, props[key]);
        }
      }
    } else if (isDebugMode) {
      // console.log(`❌ Missing prop ${key}, tried:`, possibleKeys);
    }
  });
  
  return props;
};

// ----------------
// Enhanced Error Handling
// ----------------
const handleComponentError = (error, errorInfo, widgetType, containerId) => {
  console.error(`❌ Error in widget "${widgetType}" (${containerId}):`, error);
  console.error("Component stack:", errorInfo?.componentStack);
  
  // Mark as failed
  const container = document.getElementById(containerId);
  if (container) {
    setContainerState(container, { failed: true, inProgress: false });
  }

  // Send to analytics if available
  if (window.dataLayer) {
    window.dataLayer.push({
      event: "widget_error",
      widget_type: widgetType,
      container_id: containerId,
      error_message: error.message,
      error_stack: error.stack,
    });
  }
  
  // Schedule retry if under retry limit
  const retryCount = widgetState.retryCount.get(containerId) || 0;
  if (retryCount < CONFIG.maxRetries) {
    // console.log(`🔄 Scheduling retry ${retryCount + 1}/${CONFIG.maxRetries} for ${containerId}`);
    setTimeout(() => {
      const container = document.getElementById(containerId);
      if (container) {
        resetContainerState(container);
        setContainerState(container, { retryCount: retryCount + 1 });
        initWidget(container, true);
      }
    }, CONFIG.retryDelay * (retryCount + 1)); // Exponential backoff
  }
};

// ----------------
// Enhanced UI Components
// ----------------
const createLoadingUI = (container) => {
  if (!container || container.offsetHeight < 50 || !isInViewport(container)) {
    return { remove: () => {} };
  }

  // Remove any existing loading UI
  const existingLoading = container.querySelector('.cw-widget-loading');
  if (existingLoading) {
    existingLoading.remove();
  }

  const height = Math.min(container.offsetHeight || 200, 400);
  const placeholder = document.createElement("div");
  placeholder.className = "cw-widget-loading";
  placeholder.style.cssText = `
    height: ${height}px; 
    background: rgba(0,0,0,0.03);
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
  `;

  const pulse = document.createElement("div");
  pulse.style.cssText = `
    width: 50%;
    height: 30px;
    background: rgba(0,0,0,0.1);
    border-radius: 4px;
    animation: cwPulse 1.5s ease-in-out infinite;
  `;

  if (!document.getElementById("cw-loading-keyframes")) {
    const style = document.createElement("style");
    style.id = "cw-loading-keyframes";
    style.textContent = `
      @keyframes cwPulse {
        0%, 100% { opacity: 0.3; }
        50% { opacity: 0.7; }
      }
    `;
    document.head.appendChild(style);
  }

  placeholder.appendChild(pulse);
  container.appendChild(placeholder);

  return {
    remove: () => {
      if (placeholder.parentNode === container) {
        container.removeChild(placeholder);
      }
    },
  };
};

const createErrorUI = (container, error, widgetType, containerId) => {
  const retryCount = widgetState.retryCount.get(containerId) || 0;
  const canRetry = retryCount < CONFIG.maxRetries;
  
  const errorDiv = document.createElement('div');
  errorDiv.className = 'cw-widget-error';
  errorDiv.style.cssText = `
    padding: 15px;
    border: 1px solid #f5c6cb;
    border-radius: 4px;
    background-color: #f8d7da;
    color: #721c24;
    font-family: Arial, sans-serif;
    font-size: 14px;
  `;
  
  const title = document.createElement('h3');
  title.style.cssText = 'margin: 0 0 10px 0; font-size: 16px;';
  title.textContent = `Widget Load Error: ${widgetType}`;
  
  const message = document.createElement('p');
  message.style.cssText = 'margin: 0 0 10px 0;';
  message.textContent = error.message || 'Unknown error occurred';
  
  const details = document.createElement('small');
  details.style.cssText = 'color: #856464; display: block; margin-bottom: 10px;';
  details.textContent = `Container: ${containerId} | Attempt: ${retryCount + 1}/${CONFIG.maxRetries + 1}`;
  
  errorDiv.appendChild(title);
  errorDiv.appendChild(message);
  errorDiv.appendChild(details);
  
  if (canRetry) {
    const retryButton = document.createElement('button');
    retryButton.style.cssText = `
      background: #dc3545;
      color: white;
      border: none;
      padding: 5px 10px;
      border-radius: 3px;
      cursor: pointer;
      font-size: 12px;
    `;
    retryButton.textContent = 'Retry Now';
    retryButton.onclick = () => {
      resetContainerState(container);
      setContainerState(container, { retryCount: retryCount + 1 });
      initWidget(container, true);
    };
    errorDiv.appendChild(retryButton);
  }
  
  container.innerHTML = '';
  container.appendChild(errorDiv);
};

// ----------------
// Enhanced Widget Loading
// ----------------
const loadComponent = async (widgetType) => {
  if (componentCache.has(widgetType)) {
    return componentCache.get(widgetType);
  }

  try {
    propsInspector.loadStartTime = performance.now();
    
    const importFn = widgetComponents[widgetType];
    if (!importFn) {
      throw new Error(`Widget type "${widgetType}" not registered in widgetComponents`);
    }

    // console.log(`📦 Loading component: ${widgetType}`);
    const module = await importFn();
    
    if (!module || !module.default) {
      throw new Error(`Invalid module structure for "${widgetType}": missing default export`);
    }
    
    const Component = module.default;
    componentCache.set(widgetType, Component);
    // console.log(`✅ Component loaded successfully: ${widgetType}`);
    
    return Component;
  } catch (error) {
    console.error(`❌ Failed to load component for "${widgetType}":`, error);
    
    // Clear failed cache entry
    componentCache.delete(widgetType);
    
    // Enhanced error messaging
    if (error.message.includes('Loading chunk')) {
      throw new Error(`Network error loading ${widgetType}. Please check your connection and try again.`);
    } else if (error.message.includes('not registered')) {
      throw new Error(`Widget "${widgetType}" is not properly registered in the system.`);
    } else {
      throw new Error(`Failed to load widget "${widgetType}": ${error.message}`);
    }
  }
};

// Enhanced widget initialization with better error handling and retry logic
const initWidget = async (container, force = false) => {
  if (!container) {
    console.warn('⚠️ initWidget called with null/undefined container');
    return;
  }

  if (!container.id) {
    container.id = `widget-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    // console.log(`🏷️ Generated ID for container: ${container.id}`);
  }

  const containerId = container.id;
  const state = getContainerState(container);
  
  // Skip if already initialized or in progress
  if (state.initialized || state.inProgress) {
    // console.log(`⏭️ Skipping ${containerId} (initialized: ${state.initialized}, inProgress: ${state.inProgress})`);
    return;
  }

  // Skip if failed too many times
  if (state.failed && state.retryCount >= CONFIG.maxRetries) {
    // console.log(`🚫 Max retries exceeded for ${containerId}`);
    return;
  }

  const widgetType = container.dataset.widgetType || "index_widget";
  const widget = widgetsRegistry[widgetType];

  if (!widget) {
    console.error(`❌ Widget type "${widgetType}" not found in registry`);
    createErrorUI(container, new Error(`Widget type "${widgetType}" not found`), widgetType, containerId);
    setContainerState(container, { failed: true });
    return;
  }

  // Check viewport if not forced and intersection observer is supported
  if (!force && !isInViewport(container) && supports.observer) {
    // console.log(`👀 Setting up intersection observer for ${containerId}`);
    
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          observer.disconnect();
          // console.log(`🎯 Container ${containerId} now visible, initializing...`);
          initWidget(container, true);
        }
      },
      { rootMargin: CONFIG.intersectionMargin }
    );

    observer.observe(container);
    return;
  }

  // console.log(`🚀 Initializing widget: ${widgetType} in ${containerId}`);
  setContainerState(container, { inProgress: true, failed: false });

  let loadingTimeout;
  let loadingUI;

  // Show loading UI after delay
  loadingTimeout = setTimeout(() => {
    loadingUI = createLoadingUI(container);
  }, CONFIG.loadingDelay);

  try {
    const props = extractProps(container, widget);
    // console.log(`📋 Extracted props for ${containerId}:`, props);
    
    const Component = await loadComponent(widgetType);

    clearTimeout(loadingTimeout);
    if (loadingUI) {
      loadingUI.remove();
    }

    // Double-check container is still connected to DOM
    if (!container.isConnected) {
      console.warn(`⚠️ Container ${containerId} was removed from DOM during loading`);
      setContainerState(container, { inProgress: false });
      return;
    }

    // Clean up any existing React root
    if (container.__reactRoot) {
      // console.log(`🧹 Cleaning up existing root for ${containerId}`);
      try {
        container.__reactRoot.unmount();
      } catch (unmountError) {
        console.warn(`⚠️ Error unmounting existing root:`, unmountError);
      }
      delete container.__reactRoot;
    }

    // Create new React root
    const root = createRoot(container);
    container.__reactRoot = root;

    // Track widget in Props Inspector
    propsInspector.trackWidget(containerId, widgetType, props, container);

    // Extract language settings
    const languageSettings = {
      multilingual: props.multilingual || false,
      globalSettings: props.globalSettings || window.cwLanguageSettings || {},
      fallbackLanguage: props.fallbackLanguage || 'en'
    };

    // console.log(`🎨 Rendering ${widgetType} in ${containerId}`);

    // Render the component
    root.render(
      <ErrorBoundary
        componentName={widgetType}
        containerId={containerId}
        onError={(error, errorInfo) =>
          handleComponentError(error, errorInfo, widgetType, containerId)
        }
        fallback={
          <div style={{ padding: "15px", border: "1px solid #f5c6cb", borderRadius: "4px", backgroundColor: "#f8d7da", color: "#721c24" }}>
            <h3 style={{ margin: "0 0 10px 0" }}>Widget Error</h3>
            <p>There was an error rendering this widget.</p>
            <small>Widget: {widgetType} | Container: {containerId}</small>
          </div>
        }
      >
        <div className="">
          <FrenchPrefixHandler />
          <Component {...props} />
        </div>
      </ErrorBoundary>
    );

    // Setup cleanup function with navigation awareness
    container.__cleanup = () => {
      if (widgetState.preserveWidgets || widgetState.isNavigating) {
        // console.log(`⏸️ Skipping cleanup for ${containerId} during navigation`);
        return;
      }
      
      // console.log(`🧹 Cleaning up widget ${containerId}`);
      if (container.__reactRoot) {
        try {
          container.__reactRoot.unmount();
          delete container.__reactRoot;
        } catch (error) {
          console.warn(`⚠️ Error during cleanup of ${containerId}:`, error);
        }
      }
      resetContainerState(container);
    };

    // Mark as successfully initialized
    setContainerState(container, { 
      initialized: true, 
      inProgress: false, 
      failed: false 
    });

    // console.log(`✅ Widget ${widgetType} initialized successfully in ${containerId}`);

  } catch (error) {
    console.error(`❌ Widget initialization failed for ${containerId}:`, error);
    
    clearTimeout(loadingTimeout);
    if (loadingUI) {
      loadingUI.remove();
    }

    // Create error UI
    createErrorUI(container, error, widgetType, containerId);
    
    // Update state
    setContainerState(container, { 
      inProgress: false, 
      failed: true 
    });

    // Don't auto-retry here - let the error handler manage retries
    // to avoid infinite loops
  }
};

// ----------------
// Enhanced Initialization Queue Processing
// ----------------
const processInitializationQueue = async () => {
  if (widgetState.initializationQueue.size === 0) {
    return;
  }

  // console.log(`🔄 Processing initialization queue: ${widgetState.initializationQueue.size} items`);
  
  const batch = Array.from(widgetState.initializationQueue);
  widgetState.initializationQueue.clear();
  
  // Process in small batches to avoid blocking the main thread
  const batchSize = 3;
  for (let i = 0; i < batch.length; i += batchSize) {
    const currentBatch = batch.slice(i, i + batchSize);
    
    // Process batch items in parallel
    const promises = currentBatch.map(async (containerId) => {
      const container = document.getElementById(containerId);
      if (container && !getContainerState(container).initialized) {
        try {
          await initWidget(container, false);
        } catch (error) {
          console.error(`❌ Error processing queued widget ${containerId}:`, error);
        }
      }
    });
    
    await Promise.all(promises);
    
    // Small delay between batches to prevent blocking
    if (i + batchSize < batch.length) {
      await new Promise(resolve => setTimeout(resolve, 10));
    }
  }
};

// ----------------
// Public API - Enhanced
// ----------------
window.initCustomWidget = async (containerId, widgetType = null) => {
  // console.log(`🎯 Public API: initCustomWidget(${containerId}, ${widgetType})`);
  
  let container = document.getElementById(containerId);

  // Handle multiple containers with same ID (shouldn't happen but being defensive)
  if (!container || document.querySelectorAll(`#${containerId}`).length > 1) {
    const containers = document.querySelectorAll(`#${containerId}`);

    if (containers.length > 1) {
      console.warn(`⚠️ Multiple containers found with ID: ${containerId}`);
      
      if (widgetType) {
        container = Array.from(containers).find(
          (c) => !getContainerState(c).initialized &&
          (!c.dataset.widgetType || c.dataset.widgetType === widgetType)
        );
      }

      if (!container) {
        container = Array.from(containers).find((c) => !getContainerState(c).initialized);
      }
    }
  }

  if (!container) {
    console.error(`❌ Container not found: ${containerId}`);
    return false;
  }

  // Set widget type if provided
  if (widgetType) {
    container.dataset.widgetType = widgetType;
  }

  // Check if already initialized
  const state = getContainerState(container);
  if (state.initialized || state.inProgress) {
    // console.log(`⏭️ Container ${containerId} already initialized or in progress`);
    return true;
  }

  try {
    await initWidget(container, true);
    return true;
  } catch (error) {
    console.error(`❌ Failed to initialize widget ${containerId}:`, error);
    return false;
  }
};

// ----------------
// Browser Navigation Cache Handling - Enhanced
// ----------------
const handlePageShow = (event) => {
  // console.log(`📄 Page show event - persisted: ${event.persisted}`);
  
  if (event.persisted || (window.performance && window.performance.navigation.type === 2)) {
    // console.log('🔄 Page restored from bfcache, performing full reinitialization...');
    
    // Reset all widget state
    widgetState.initialized.clear();
    widgetState.inProgress.clear();
    widgetState.failed.clear();
    widgetState.retryCount.clear();
    widgetState.initializationQueue.clear();
    
    const containers = document.querySelectorAll('[class*="custom-"][class*="-widget-container"]');
    
    Array.from(containers).forEach((container) => {
      resetContainerState(container);
      
      // Clear loading indicators
      const loadingElements = container.querySelectorAll('.cw-widget-loading');
      loadingElements.forEach(loading => loading.remove());
      
      // Queue for reinitialization
      if (container.id) {
        widgetState.initializationQueue.add(container.id);
      }
    });
    
    // Process reinitialization
    setTimeout(() => {
      performFullReinitialization();
    }, 100);
  }
};

const handlePopState = () => {
  // console.log('🔙 Popstate event - checking for uninitialized widgets');
  
  setTimeout(() => {
    const containers = document.querySelectorAll('[class*="custom-"][class*="-widget-container"]');
    
    Array.from(containers).forEach((container) => {
      const state = getContainerState(container);
      if (!state.initialized && !state.inProgress && container.id) {
        widgetState.initializationQueue.add(container.id);
      }
    });
    
    processInitializationQueue();
  }, 50);
};

const handleVisibilityChange = () => {
  if (!document.hidden) {
    // console.log('👁️ Page became visible, checking widgets...');
    
    setTimeout(() => {
      const containers = document.querySelectorAll('[class*="custom-"][class*="-widget-container"]');
      
      Array.from(containers).forEach((container) => {
        const state = getContainerState(container);
        if (!state.initialized && !state.inProgress && container.id) {
          widgetState.initializationQueue.add(container.id);
        }
      });
      
      processInitializationQueue();
    }, 100);
  }
};

// ----------------
// Full Reinitialization Function
// ----------------
const performFullReinitialization = async () => {
  // console.log('🔄 Performing full widget reinitialization...');
  
  // Clear component cache to ensure fresh loads
  componentCache.clear();
  
  // Find all widget containers
  const containers = document.querySelectorAll('[class*="custom-"][class*="-widget-container"]');
  // console.log(`📊 Found ${containers.length} widget containers`);
  
  // Reset state and queue all containers
  Array.from(containers).forEach((container) => {
    if (!container.id) {
      container.id = `widget-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    }
    
    resetContainerState(container);
    widgetState.initializationQueue.add(container.id);
  });
  
  // Mark as ready and process queue
  widgetState.isReady = true;
  await processInitializationQueue();
  
  // console.log('✅ Full reinitialization complete');
};

// ----------------
// Enhanced Debug Tools
// ----------------
window.debugWidget = {
  listWidgets: () => {
    const containers = document.querySelectorAll('[class*="custom-"][class*="-widget-container"]');
    return Array.from(containers).map((container) => {
      const state = getContainerState(container);
      return {
        id: container.id,
        widgetType: container.dataset.widgetType,
        state: state,
        hasRoot: !!container.__reactRoot,
        isVisible: isInViewport(container),
        inQueue: widgetState.initializationQueue.has(container.id),
        propsInspected: propsInspector.inspectedWidgets.has(container.id),
        performance: propsInspector.performanceData.get(container.id)
      };
    });
  },
  
  reloadWidget: async (containerId) => {
    const container = document.getElementById(containerId);
    if (!container) {
      console.error(`❌ Container ${containerId} not found`);
      return false;
    }
    
    // console.log(`🔄 Manually reloading widget: ${containerId}`);
    resetContainerState(container);
    
    try {
      await initWidget(container, true);
      return true;
    } catch (error) {
      console.error(`❌ Failed to reload widget ${containerId}:`, error);
      return false;
    }
  },
  
  reloadAllWidgets: async () => {
    // console.log('🔄 Manually reloading all widgets...');
    await performFullReinitialization();
  },
  
  getWidgetProps: (containerId) => {
    const container = document.getElementById(containerId);
    if (!container) return null;

    const widgetType = container.dataset.widgetType || "index_widget";
    const widget = widgetsRegistry[widgetType];
    if (!widget) return null;

    return extractProps(container, widget);
  },
  
  inspectWidget: (containerId) => {
    const widget = propsInspector.inspectedWidgets.get(containerId);
    const state = getContainerState(document.getElementById(containerId));
    
    if (widget) {
      // console.log(`🔍 Props for ${containerId}:`, widget.props);
      // console.log(`📊 Performance:`, propsInspector.performanceData.get(containerId));
      // console.log(`🔄 Render count:`, propsInspector.renderCount.get(containerId));
      // console.log(`🏷️ State:`, state);
    } else {
      // console.log(`❌ Widget ${containerId} not found in inspector`);
    }
  },
  
  getPerformanceReport: () => {
    const report = [];
    propsInspector.inspectedWidgets.forEach((widget, id) => {
      const perf = propsInspector.performanceData.get(id);
      const state = getContainerState(document.getElementById(id));
      
      report.push({
        id,
        widgetType: widget.widgetType,
        score: perf?.score || 0,
        renders: propsInspector.renderCount.get(id) || 0,
        warnings: perf?.warnings || [],
        state: state
      });
    });
    
    report.sort((a, b) => a.score - b.score);
    console.table(report);
    return report;
  },
  
  getSystemStatus: () => {
    return {
      isReady: widgetState.isReady,
      preserveWidgets: widgetState.preserveWidgets,
      isNavigating: widgetState.isNavigating,
      queueSize: widgetState.initializationQueue.size,
      componentCacheSize: componentCache.size,
      totalInitialized: widgetState.initialized.size,
      totalInProgress: widgetState.inProgress.size,
      totalFailed: widgetState.failed.size,
      config: CONFIG
    };
  },
  
  clearCache: () => {
    componentCache.clear();
    // console.log('🧹 Component cache cleared');
  }
};

// ----------------
// Event Listeners
// ----------------
window.addEventListener('pageshow', handlePageShow);
window.addEventListener('popstate', handlePopState);
document.addEventListener('visibilitychange', handleVisibilityChange);

// ----------------
// Legacy API Support
// ----------------
Object.keys(widgetComponents).forEach((type) => {
  const camelCase = type.replace(/_([a-z])/g, (g) => g[1].toUpperCase());
  const fnName = `initCustom${camelCase.charAt(0).toUpperCase() + camelCase.slice(1)}`;
  window[fnName] = (containerId) => window.initCustomWidget(containerId, type);
});

// ----------------
// Main Initialization Logic
// ----------------
const initVisibleWidgets = async () => {
  // console.log('🔍 Scanning for visible widgets...');
  
  const containers = document.querySelectorAll('[class*="custom-"][class*="-widget-container"]');
  // console.log(`📊 Found ${containers.length} widget containers`);

  // Queue uninitialized containers
  Array.from(containers).forEach((container) => {
    if (!container.id) {
      container.id = `widget-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    }
    
    const state = getContainerState(container);
    if (!state.initialized && !state.inProgress) {
      widgetState.initializationQueue.add(container.id);
    }
  });

  // Process the queue
  await processInitializationQueue();
};

const debouncedInit = debounce(() => {
  if (!widgetState.preserveWidgets && !widgetState.isNavigating) {
    initVisibleWidgets();
  }
}, CONFIG.debounceTime);

const initializeWidgets = async () => {
  // console.log('🚀 Initializing widget system...');
  
  // Mark system as ready
  widgetState.isReady = true;
  
  // Initialize navigation handlers
  handleElementorNavigation();
  handleLinkClicks();

  // Initial widget scan
  await initVisibleWidgets();

  // Setup scroll and resize handlers
  window.addEventListener("scroll", debouncedInit, { passive: true });
  window.addEventListener("resize", debouncedInit, { passive: true });

  // Cleanup handler
  window.addEventListener("beforeunload", () => {
    // console.log('🔄 Page unloading, cleaning up...');
    
    window.removeEventListener("scroll", debouncedInit);
    window.removeEventListener("resize", debouncedInit);
    window.removeEventListener('pageshow', handlePageShow);
    window.removeEventListener('popstate', handlePopState);
    document.removeEventListener('visibilitychange', handleVisibilityChange);
    
    // Only cleanup widgets if it's a real page unload
    if (!widgetState.preserveWidgets && !widgetState.isNavigating) {
      document.querySelectorAll('[class*="custom-"][class*="-widget-container"]')
        .forEach((container) => {
          if (container.__cleanup) {
            container.__cleanup();
          }
        });
    }
  });
  
  // console.log('✅ Widget system initialized');
};

// ----------------
// Startup Logic
// ----------------
const startWidgetSystem = () => {
  if (supports.idle) {
    window.requestIdleCallback(() => initializeWidgets(), {
      timeout: CONFIG.idleTimeout,
    });
  } else {
    setTimeout(initializeWidgets, 10);
  }
};

// Start the system when DOM is ready
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", startWidgetSystem);
} else {
  startWidgetSystem();
}

// ----------------
// Elementor Integration - Enhanced
// ----------------
if (window.elementorFrontend) {
  // console.log('🎨 Elementor detected, setting up hooks...');
  
  // Create specific handlers for each widget type
  const handleElementorWidget = (widgetType) => (element) => {
    // Handle both jQuery and DOM elements
    const containerEl = element.find ? 
      element.find('[class*="custom-"][class*="-widget-container"]')[0] :
      element.querySelector('[class*="custom-"][class*="-widget-container"]');
    
    if (containerEl) {
      if (!containerEl.id) {
        containerEl.id = `widget-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      }
      
      containerEl.dataset.widgetType = widgetType;
      // console.log(`🎯 Elementor widget ready: ${widgetType} in ${containerEl.id}`);
      
      // Add to queue for processing
      widgetState.initializationQueue.add(containerEl.id);
      
      // Process queue after short delay
      setTimeout(() => processInitializationQueue(), 50);
    }
  };

  // Register handlers for all widget types
  Object.keys(widgetsRegistry).forEach((widgetType) => {
    const hookName = `frontend/element_ready/${widgetType}.default`;
    // console.log(`🔗 Registering Elementor hook: ${hookName}`);
    
    window.elementorFrontend.hooks.addAction(
      hookName,
      handleElementorWidget(widgetType)
    );
  });

  // Global element ready handler
  window.elementorFrontend.hooks.addAction('frontend/element_ready/global', () => {
    setTimeout(() => {
      if (!widgetState.preserveWidgets && !widgetState.isNavigating) {
        initVisibleWidgets();
      }
    }, 50);
  });
}

// ----------------
// Initialize Mutation Observer
// ----------------
observeContainerChanges();

// ----------------
// Global Language Settings
// ----------------
window.setGlobalLanguageSettings = (settings) => {
  // console.log('🌍 Setting global language settings:', settings);
  window.cwLanguageSettings = settings;
  
  if (settings.reInitializeOnChange) {
    // console.log('🔄 Reinitializing widgets for language change...');
    performFullReinitialization();
  }
};

// ----------------
// AJAX Navigation Support
// ----------------
const handleAjaxNavigation = () => {
  const ajaxEvents = ['pjax:start', 'turbo:before-visit', 'htmx:beforeRequest'];
  
  ajaxEvents.forEach(eventName => {
    document.addEventListener(eventName, () => {
      // console.log(`🔄 ${eventName} detected, preserving widgets...`);
      widgetState.preserveWidgets = true;
      widgetState.isNavigating = true;
    });
  });

  const ajaxCompleteEvents = ['pjax:end', 'turbo:load', 'htmx:afterRequest'];
  
  ajaxCompleteEvents.forEach(eventName => {
    document.addEventListener(eventName, () => {
      // console.log(`✅ ${eventName} detected, reinitializing widgets...`);
      setTimeout(() => {
        widgetState.preserveWidgets = false;
        widgetState.isNavigating = false;
        performFullReinitialization();
      }, 100);
    });
  });
};

handleAjaxNavigation();

// console.log('🎉 Widget bridge system loaded successfully!');